import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { isPresent } from '@ember/utils'
import { Interval } from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import { serialize as serializeSort, deserialize as deserializeSort } from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type SessionService from 'teacher-ui-client/services/session'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import type Student from 'teacher-ui-client/models/student'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import DurationUnitCell from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import type { CellObject, RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { BaseTableActions } from 'teacher-ui-client/components/base/table-actions'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type {
  SimpleSummaryStudent,
  SimpleSummaryTotals,
} from 'teacher-ui-client/utils/student-events/fetchers/simple-summary'

interface Args {
  product: string
  totalsData?: SimpleSummaryTotals
  students?: Student[]
  studentsData?: SimpleSummaryStudent[]
}

type DataKey =
  | 'firstName'
  | 'lastName'
  | 'externalId'
  | 'gradeName'
  | 'totalTime'
  | 'lessonsCompleted'
  | 'lexileGrowth'
  | 'gradePosition'

type DataKeyObject = Partial<Record<DataKey, string>>

function headerClasses(product: string, dataKey: DataKey) {
  const defaultClass = product === 'maths' ? 'bg-ms-green-100' : 'bg-oceany-blue-100'
  const mapping: DataKeyObject = {
    totalTime: 'w-1/4 bg-purply-pink-350 text-white',
    lessonsCompleted: 'w-1/4 bg-juicy-orange-350 text-white',
    lexileGrowth: 'w-1/4 bg-wl-blue-250 text-white',
  }
  return mapping[dataKey] ?? defaultClass
}

function cellClasses(product: string, dataKey: DataKey) {
  const colorClass =
    product === 'maths'
      ? 'bg-ms-green-50 group-hover:bg-ms-green-100'
      : 'bg-oceany-blue-50 group-hover:bg-oceany-blue-100'
  const mapping: DataKeyObject = {
    totalTime: 'text-center',
    lessonsCompleted: 'text-center',
    lexileGrowth: 'text-center',
  }
  const customClass = mapping[dataKey] ?? ''
  return `${colorClass} ${customClass}`
}

function footerCellClasses(product: string, dataKey: DataKey) {
  const defaultClass = 'table-footer-cell border-t print:border-black sticky bg-white print:static'
  const colorClass = product === 'maths' ? 'border-ms-green-100' : 'border-oceany-blue-100'
  const mapping: DataKeyObject = {
    firstName: 'whitespace-nowrap', // stops the tooltips from wrapping
    totalTime: 'text-center',
    lessonsCompleted: 'text-center',
    lexileGrowth: 'text-center',
  }
  const customClass = mapping[dataKey] ?? ''
  return `${defaultClass} ${colorClass} ${customClass}`
}

const comparersForColumns: DataKeyObject = {
  firstName: 'sortByValue',
  lastName: 'sortByValue',
  gradePosition: 'sortByNumber', // sortKey
  totalTime: 'sortByNumber',
  lessonsCompleted: 'sortByNumber',
  lexileGrowth: 'sortByNumber',
}

interface Signature {
  Args: Args
}

export class SummaryTablesStudents extends Component<Signature> {
  @service intl!: IntlService

  @service session!: SessionService

  @service subscriptionType!: SubscriptionTypeService

  @service gradeSets!: GradeSetsService

  @tracked sort?: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  onSortClick(sortingConfig: SortingConfig) {
    this.sortingConfig = sortingConfig
  }

  get isSubco() {
    return this.subscriptionType.current && this.session.currentTeacher.subcoFor(this.subscriptionType.current)
  }

  /**
   * An array of keys used to identify data columns, also serving as the key/path to data within a row object.
   */
  get dataKeys(): DataKey[] {
    const dataKeys: DataKey[] = ['firstName', 'lastName', 'externalId', 'gradeName', 'totalTime', 'lessonsCompleted']
    return this.args.product === 'maths' ? dataKeys : [...dataKeys, 'lexileGrowth']
  }

  /**
   * Transforms an array of data keys into column objects. The column objects are used by the table to determine where
   * to get data from for each cell in the column, how to sort that column, etc
   *
   * See the BasicTable readme for more info on column objects
   */
  get columns() {
    const { dataKeys, durationConfig, intl } = this

    return dataKeys.map((dataKey) => {
      const value = intl.t(`reporting.summaryReportTitles.${this.args.product}.${dataKey}`)

      const isGradeName = dataKey === 'gradeName'
      const isTotalTime = dataKey === 'totalTime'
      const isTimeColumn = isTotalTime
      const isExternalId = dataKey === 'externalId'

      const replaceZeroColumn = ['lessonsCompleted', 'lexileGrowth'].includes(dataKey)

      return {
        value,
        valuePath: dataKey,
        component: SortableCell,
        comparerKey: comparersForColumns[dataKey],
        sortKey: isGradeName ? 'gradePosition' : undefined,
        cellComponent: isTimeColumn ? DurationUnitCell : undefined,
        durationConfig: isTimeColumn ? durationConfig : undefined,
        formatEmptyConfig: replaceZeroColumn ? { replaceZero: true } : undefined,
        classNames: `table-box table-box-header ${headerClasses(this.args.product, dataKey)}${
          isExternalId ? ' hidden' : ''
        }`,
        cellClassNames: `table-box table-box-cell ${cellClasses(this.args.product, dataKey)}${
          isExternalId ? ' hidden' : ''
        }`,
      }
    })
  }

  /**
   * Defines how the durations should be formatted
   */
  get durationConfig() {
    const splitDurationOptions = {
      max: Interval.Hours,
      min: Interval.Minutes,
      maxIntervals: 2,
    }
    const dataInterval = 'seconds'
    return { dataInterval, splitDurationOptions }
  }

  /**
   * Map each student to a row object, each row containing the keys (with data) that match what the column will use to
   * retrieve it
   */
  get rows() {
    const students = this.args.students ?? []
    const studentsData = this.args.studentsData ?? []
    const { dataKeys } = this

    return students.map(({ id, firstName, lastName, externalId, gradePosition }) => {
      const gradeName = isPresent(gradePosition) ? this.gradeSets.findByPosition(gradePosition)?.name : '-'

      const data: Partial<SimpleSummaryStudent> =
        studentsData.find((studentData) => String(studentData.student_id) === id) ?? {}

      // We don't need any per-cell level configuration, so just use an empty array of cellObjects
      const cells = Array(dataKeys.length).fill({})

      return {
        firstName,
        lastName,
        externalId,
        gradeName,
        gradePosition, // sortKey for sorting the grade name
        totalTime: data.total_time,
        lessonsCompleted: data.lessons_completed,
        lexileGrowth: data.lexile_growth,
        // classes for row striping and hovering the row of cells as a group (via tailwind group-hover)
        classNames: 'table-row-striped group',
        cells,
      }
    })
  }

  /**
   * This returns an object of data keys that map to cellObjects for the average row of the footer
   */
  get averageCellsForColumns(): Partial<Record<DataKey, CellObject>> {
    const { totalsData } = this.args
    const { durationConfig, averageTooltipMessage, intl } = this

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    return {
      firstName: {
        component: TooltipCell,
        value: intl.t(`reporting.summaryReportTitles.${this.args.product}.average`),
        tooltipValue: averageTooltipMessage,
      },
      lastName: emptyCell,
      externalId: emptyCell,
      gradeName: emptyCell,
      totalTime: {
        component: DurationUnitCell,
        durationConfig,
        value: totalsData?.average_total_time_per_student,
      },
      lessonsCompleted: { value: totalsData?.average_lessons_completed_per_student },
      lexileGrowth: { value: totalsData?.average_lexile_growth },
    }
  }

  /**
   * This returns an object of data keys that map to cellObjects for the totals row of the footer
   */
  get totalCellsForColumns(): Partial<Record<DataKey, CellObject>> {
    const { totalsData } = this.args
    const { durationConfig, isSubco, totalsTooltipMessage, intl } = this

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    return {
      firstName: {
        component: isSubco ? TooltipCell : undefined,
        value: intl.t(`reporting.summaryReportTitles.${this.args.product}.totals`),
        tooltipValue: isSubco ? totalsTooltipMessage : undefined,
      },
      lastName: emptyCell,
      externalId: emptyCell,
      gradeName: emptyCell,
      totalTime: {
        component: DurationUnitCell,
        durationConfig,
        value: totalsData?.total_time,
      },
      lessonsCompleted: { value: totalsData?.lessons_completed },
      lexileGrowth: { value: intl.t('reporting.summaryReportTitles.na') },
    }
  }

  /**
   * This returns an array of rowObjects to display in the footer of the table
   */
  get footerRows(): RowObject[] {
    const averageCells = this.dataKeys.map((dataKey) => {
      const cell = this.averageCellsForColumns[dataKey]
      const hiddenClass = dataKey === 'externalId' ? ' hidden' : ''
      return { ...cell, classNames: footerCellClasses(this.args.product, dataKey) + ' bottom-[40px]' + hiddenClass }
    })
    const averageRow = { cells: averageCells }

    const totalsCells = this.dataKeys.map((dataKey) => {
      const cell = this.totalCellsForColumns[dataKey]
      const hiddenClass = dataKey === 'externalId' ? ' hidden' : ''
      return { ...cell, classNames: footerCellClasses(this.args.product, dataKey) + ' bottom-0' + hiddenClass }
    })
    const totalsRow = { cells: totalsCells }

    return [averageRow, totalsRow]
  }

  /**
   * @memberOf mathsSummaryTable
   * @returns {Array} a list of messages to be displayed in the footer with each message in a new row
   */
  get totalsTooltipMessage() {
    return this.intl.t('reporting.summaryReportTitles.studentTotalToolTipMessage')
  }

  get averageTooltipMessage() {
    return this.intl.t('reporting.summaryReportTitles.studentAverageToolTipMessage')
  }

  <template>
    <div class="max-h-[700px] overflow-auto print:max-h-none print:overflow-y-visible">
      <BaseTableActions
        @items={{this.rows}}
        @columns={{this.columns}}
        @footerRows={{this.footerRows}}
        @sortingConfig={{this.sortingConfig}}
        @onSortClick={{this.onSortClick}}
        as |Table|
      >
        <Table exportable data-test-summary-table class="relative w-full border-separate border-spacing-0" as |tbl|>
          <tbl.head class="print:table-row-group print:text-xs" as |h|>
            <h.row as |r|>
              <r.cell class="sticky top-0 print:static" />
            </h.row>
          </tbl.head>
          <tbl.body />
          <tbl.foot class="print:table-row-group print:border-t-2 print:border-black print:text-xs" />
        </Table>
      </BaseTableActions>
    </div>
  </template>
}
export default SummaryTablesStudents
